import type {
    FilterMetadataMap,
    FromFilterDefinition,
    NonNullableFilterValues,
    NullableFilterValues,
} from "../filtering";
import {SortSelectionObject} from "../common";

export const WORK_ORDER_SORT_COLUMNS = {
    statusDisplay: 'statusDisplay',
    scheduleStart: 'scheduleStart',
    syncStatus: 'syncStatus'
} as const;

export const WORK_ORDER_DEFAULT_SORT: SortSelectionObject<typeof WORK_ORDER_SORT_COLUMNS> = {
    column: 'scheduleStart',
    direction: 'desc'
}

export const WORK_ORDER_FILTER_DEFINITION = {
    search: 'string',
    client: 'string[]',
    project: 'string[]',
    template: 'string[]',
    jobSite: 'string[]',
    city: 'string[]',
    state: 'string[]',
    status: 'string[]',
    serviceDate: 'DateFilter',
    hasOpenProblem: 'boolean',
    hasOpenSupportCase: 'boolean',
    syncStatus: 'string[]',
    internalStatus: 'string[]',
    soStatus: 'string[]',
    approvalStatus: 'string[]',
    type: 'string[]',
    liftStatus: 'string[]',
    materialStatus: 'string[]',
} as const;

export const WORK_ORDER_STATUS_VALUES = [
    "Draft",
    "Published",
    "Routed",
    "Assigned",
    "Start Time Set",
    "Confirmed",
    "On my way",
    "Assigned: At Risk",
    "Assigned: Cancelled",
    "Assigned: Delayed",
    "Checked in",
    "Checked out",
    "On Hold",
    "Work Done",
    "Work Done: Increase Requested",
    "Approved",
    "Paid"
] as const;

export const WORK_ORDER_SYNC_STATUS_VALUES = [
    'PENDING',
    'SYNCED',
    'PARTIALLY_SYNCED',
    'SKIPPED',
    'FAILED'
] as const;

export const WORK_ORDER_INTERNAL_STATUS_VALUES = [
    '1. In Progress',
    '2a. Checked Out-Open Issues-WL',
    '3. Checked out - No Issues',
    '4. Closeout Notes Complete',
    '5. Cancelled',
    '6. Finance Question ',
    '2b. Checked Out-Open Issues-Client',
    '4b. Verified Close Out Notes Complete',
] as const;

export const WORK_ORDER_SO_STATUS_VALUES = [
    '1. Not Created/Not Added',
    '2. Created/Added',
    '3a. Question for WO Manger',
    '3b. Question for PM',
    '6a. Invoiced',
    '9. Cancelled',
    '7a. Warranty',
    '8. Included',
    '4. Given to PM',
    '5. Given to Finance',
    '7b. External Overrun - Not Confirmed',
    '6b. Included and Invoiced',
    '7b2. External Overrun - Confirmed',
    '7c. Internal Overrun - Not Confirmed',
    '7c2. Internal Overun - Confirmed',
    '10. Change Order Pending',
] as const;

export const WORK_ORDER_APPROVAL_STATUS_VALUES = [
    '1. Not Ready',
    '2. Ready',
    '3. Not Ready - Additional Costs Pending',
    '4. Confirmed for Approval',
] as const;

export const WORK_ORDER_TYPE_VALUES = [
    'Initial Visit',
    'Abort - Billable',
    'Abort - Not Billable',
    'Revisit - Billable',
    'Revisit - Not Billable',
    'Reschedule',
    'Warranty',
    'SCM',
    'Materials',
    'Lift',
] as const;

export const WORK_ORDER_LIFT_STATUS_VALUES = [
    'Not Applicable',
    'RFQ',
    'Quoted',
    'Ordered',
    'On Site',
    'Called Off',
    'Picked Up',
] as const;

export const WORK_ORDER_MATERIAL_STATUS_VALUES = [
    'Action Needed',
    'RFQ',
    'Ordered',
    'Tracking',
    'Delivered',
    'Issue',
    'Returned to Sender',
] as const;

export const WORK_ORDER_FILTER_METADATA = {
    status: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_STATUS_VALUES,
        isArray: true,
    },
    syncStatus: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_SYNC_STATUS_VALUES,
        isArray: true
    },
    internalStatus: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_INTERNAL_STATUS_VALUES,
        isArray: true,
    },
    soStatus: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_SO_STATUS_VALUES,
        isArray: true,
    },
    approvalStatus: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_APPROVAL_STATUS_VALUES,
        isArray: true,
    },
    type: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_TYPE_VALUES,
        isArray: true,
    },
    liftStatus: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_LIFT_STATUS_VALUES,
        isArray: true,
    },
    materialStatus: {
        kind: 'enum' as const,
        enumValues: WORK_ORDER_MATERIAL_STATUS_VALUES,
        isArray: true,
    },
} as const satisfies FilterMetadataMap<typeof WORK_ORDER_FILTER_DEFINITION>;

export type WorkOrderFilterDefinition = FromFilterDefinition<typeof WORK_ORDER_FILTER_DEFINITION>


export type NullableWorkOrderFilter =
    NullableFilterValues<WorkOrderFilterDefinition>;
export type NonNullableWorkOrderFilter =
    NonNullableFilterValues<WorkOrderFilterDefinition>;

export const DEFAULT_WORK_ORDER_FILTER
    :
    NullableWorkOrderFilter = {
    search: null,
    client: null,
    project: null,
    template: null,
    jobSite: null,
    city: null,
    state: null,
    status: null,
    serviceDate: null,
    syncStatus: null,
    hasOpenProblem: null,
    hasOpenSupportCase: null,
    internalStatus: null,
    soStatus: null,
    approvalStatus: null,
    type: null,
    liftStatus: null,
    materialStatus: null,
};
